import { getTopic, seasonWord, seasonWindow, type SeasonWord } from '@gogrov/shared'
import type { ControlPill } from '@/types/controls'

/**
 * A pill's state on Topics (Brian's settings-topics.html stateOf / typeOf):
 * anchor, researching, waiting, resting (by hand or out of season), warming,
 * in season, closing, or plain. Pure, so the sheet and the cards agree.
 */

export type PillStateName = 'anchor' | 'researching' | 'waiting' | 'resting' | 'warming' | 'in' | 'closing' | 'plain'

export interface Reading {
  type: 'specific' | 'broad' | 'watching'
  trend: 'up' | 'down' | 'flat' | null
  /** 0 none, 1 thin, 2 moderate, 3 strong. Never shown as a number. */
  band: 0 | 1 | 2 | 3
  /** Days since anyone researched it. */
  ago: number
  note: string | null
}

export interface Season { open: string; close: string; ramp: number; lead: number; next: string[] }
export interface Win { open: Date; close: Date; wake: Date }

export interface PillState {
  s: PillStateName
  r: Reading | null
  sea: Season | null
  w: Win | null
  /** For resting: back on this day. */
  until?: Date
  byHand?: boolean
}

export const DAY = 864e5
export const STALE_DAYS = 60
export const addD = (d: Date, n: number) => new Date(d.getTime() + n * DAY)
export const iso = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
export const day = (s: string) => new Date(`${s.slice(0, 10)}T12:00:00`)
export const fmt = (d: Date) => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
export const fmtNum = (d: Date) => `${d.getMonth() + 1}/${d.getDate()}`
export const today = () => day(iso(new Date()))

/** The research reading, if anyone has one. */
export function readingOf(p: ControlPill, now: Date): Reading | null {
  if (!p.researched_at || p.pill_type === 'anchor' || !p.pill_type) return null
  const type = p.pill_type
  const n = p.story_count
  const band = (type === 'watching' ? 0 : n == null ? 2 : n <= 0 ? 0 : n < 5 ? 1 : n < 20 ? 2 : 3) as Reading['band']
  return { type, trend: type === 'specific' ? (p.trend ?? 'flat') : null, band, ago: Math.max(0, Math.floor((now.getTime() - new Date(p.researched_at).getTime()) / DAY)), note: p.note }
}

const fromWord = (sw: SeasonWord): Season => ({ open: sw.open, close: sw.close, ramp: sw.ramp, lead: sw.lead, next: sw.next })

/** Only the 13 seasonal topics carry a season: a season word there is trusted
 *  at once; a season Emma found needs a specific pill. */
export function seasonOf(p: ControlPill, r: Reading | null): Season | null {
  if (!getTopic(p.topic)?.seasonal) return null
  const sw = seasonWord(p.term)
  if (sw) return fromWord(sw)
  if (r?.type === 'specific' && p.window_open && p.window_close) {
    return { open: p.window_open, close: p.window_close, ramp: 21, lead: p.close_lead_days ?? 14, next: p.successors ?? [] }
  }
  return null
}

export function stateOf(p: ControlPill, now: Date, extra: { researching?: Set<string>; waiting?: Set<string> } = {}): PillState {
  if (p.pill_type === 'anchor') return { s: 'anchor', r: null, sea: null, w: null }
  if (extra.researching?.has(p.id)) return { s: 'researching', r: null, sea: null, w: null }
  const r = readingOf(p, now)
  const sea = seasonOf(p, r)
  const w = sea ? seasonWindow(sea, now) : null
  if (extra.waiting?.has(p.id)) return { s: 'waiting', r, sea, w }
  if (p.rest_until && now < day(p.rest_until)) return { s: 'resting', until: day(p.rest_until), byHand: true, r, sea, w }
  if (!sea || !w) return { s: 'plain', r, sea: null, w: null }
  if (now >= w.open && now <= w.close) return { s: now >= addD(w.close, -sea.lead) ? 'closing' : 'in', r, sea, w }
  if (now >= w.wake) return { s: 'warming', r, sea, w }
  if (p.force_awake) return { s: 'plain', r, sea, w }
  return { s: 'resting', until: w.wake, byHand: false, r, sea, w }
}

/** The date the clock suggests: when coverage starts building next time round,
 *  or four weeks for a pill with no season. */
export function suggestedWake(p: ControlPill, now: Date): Date {
  const st = stateOf({ ...p, rest_until: null, force_awake: false }, now)
  if (st.sea && st.w) {
    if (st.s === 'resting') return st.w.wake
    return seasonWindow(st.sea, addD(st.w.close, 1)).wake
  }
  return addD(now, 28)
}

/** The pill's type (GoGrov-Demo-v2): inferred, never set by the creator. */
export function typeOf(p: ControlPill, st: PillState): { c: string; l: string } {
  if (p.pill_type === 'anchor') return { c: 't-anchor', l: 'Low volume, steady' }
  const r = st.r
  if (!r) return { c: 't-unres', l: 'not researched' }
  if (r.type === 'watching' || !r.band) return { c: 't-watch', l: 'Not enough coverage yet' }
  if (r.type === 'broad') return { c: 't-broad', l: 'Too broad' }
  if (r.trend === 'up') return { c: 't-up', l: 'Well targeted ↑' }
  if (r.trend === 'down') return { c: 't-dn', l: 'Well targeted ↓' }
  return { c: 't-flat', l: 'Well targeted' }
}

export const isStale = (p: ControlPill, st: PillState) => p.pill_type !== 'anchor' && !!st.r && st.r.ago > STALE_DAYS
export const needsResearch = (p: ControlPill, st: PillState) =>
  p.pill_type !== 'anchor' && st.s !== 'researching' && (!st.r || isStale(p, st))

/** Counts against the pool: everything but resting pills, places and the waiting. */
export const counts = (s: PillStateName) => s !== 'resting' && s !== 'anchor' && s !== 'waiting'

/**
 * A pill coming back to a full pool waits (READY · no room). The ones that came
 * back last wait first.
 */
export function waitingIds(pills: ControlPill[], now: Date, cap: number): Set<string> {
  const active = pills
    .map((p) => ({ p, st: stateOf(p, now) }))
    .filter((x) => counts(x.st.s))
  const over = active.length - cap
  if (over <= 0) return new Set()
  const back = (p: ControlPill) => (p.rest_until ? day(p.rest_until).getTime() : 0)
  return new Set(active
    .filter((x) => x.p.rest_until || x.st.sea)
    .sort((a, b) => back(b.p) - back(a.p) || b.p.created_at.localeCompare(a.p.created_at))
    .slice(0, over).map((x) => x.p.id))
}

export const cap1 = (s: string) => s.charAt(0).toUpperCase() + s.slice(1)
export const BAND_WORDS = ['No coverage found', 'Thin coverage', 'Moderate coverage', 'Strong coverage'] as const
export const BAND_SHORT = ['no', 'thin', 'moderate', 'strong'] as const
